/**
 * The Norican corporate logo, from the Norican Brand Guidelines 3.0 (2026-10-08).
 *
 * The files in public/brand/ are verbatim copies of the supplied masters (Norican
 * intranet, Communications & Branding / Logos). The guide forbids recreating or
 * altering them, so the component only picks a file and a height; it never tints,
 * crops or pads the artwork. Every file already carries its exclusion zone.
 *
 * `kind` picks the asset for the space available (guide p.13 primary, p.14 secondary,
 * p.15 icon mark, p.16 exclusion zones, p.17 minimum sizes):
 *   - `primary`   logo with the "Metal. Made. Possible." tagline, for where people
 *                 first meet the brand (login). Minimum 35 px of artwork, which is
 *                 a 54 px box because the SVG's built-in clear space is 35 % of it.
 *   - `secondary` logo without the tagline, for small formats. Minimum 10 px of
 *                 artwork, which is a 19 px box. Supplied only as PNG (1113 x 294),
 *                 used at native resolution and scaled down, never up.
 *   - `mark`      the icon mark alone, where the full logo is too large. Minimum
 *                 10 px; the SVG has no built-in padding, so callers keep a gap of
 *                 at least a quarter of its height to neighbouring content.
 *
 * `variant`:
 *   - `auto`  (default) follows the app theme: white in dark mode, Norican Blue in light.
 *   - `white` for a ground that is always Norican Blue (the sidebar).
 *   - `dark`  for a ground that is always light.
 *
 * `height` is the only way to size the logo. It is one of a fixed set of box heights,
 * each mapped to a static Tailwind class, so the rendered height always equals the
 * height the minimum-size guard checks. The width follows the artwork's own aspect
 * ratio via `w-auto`, so the logo is never stretched. `className` is for spacing only;
 * a height class in it is ignored for sizing purposes and logs a `[brand]` warning in dev.
 */

import { cn } from "@norican/react";

import { useAppTheme } from "@/contexts/theme";
import { logger } from "@/lib/logger";

export type NoricanLogoKind = "primary" | "secondary" | "mark";

/**
 * Allowed box heights in px, each with a static class so Tailwind compiles it.
 * Add an entry here to support a new size; never size the logo through `className`.
 */
const HEIGHT_CLASS = {
  10: "h-2.5",
  16: "h-4",
  19: "h-[19px]",
  20: "h-5",
  24: "h-6",
  32: "h-8",
  40: "h-10",
  54: "h-[54px]",
  64: "h-16",
} as const;

export type NoricanLogoHeight = keyof typeof HEIGHT_CLASS;

/** Matches a Tailwind height utility (h-6, h-[54px], md:h-8, …) in a class string. */
const HEIGHT_UTILITY = /(^|\s)([a-z0-9-]+:)*!?h-/;

interface LogoAsset {
  src: { white: string; dark: string };
  /** Intrinsic width / height of the supplied file. */
  aspectRatio: number;
  /** Smallest box height in px that keeps the visible artwork at the guide's minimum (p.17). */
  minBoxHeight: NoricanLogoHeight;
  /** Height used when the caller passes none. */
  defaultHeight: NoricanLogoHeight;
}

const ASSETS: Record<NoricanLogoKind, LogoAsset> = {
  primary: {
    src: {
      white: "/brand/norican-primary-2026-white.svg",
      dark: "/brand/norican-primary-2026-blue.svg",
    },
    // viewBox 345.78 x 119.97
    aspectRatio: 2.8822,
    minBoxHeight: 54,
    defaultHeight: 54,
  },
  secondary: {
    src: {
      white: "/brand/norican-secondary-2026-white.png",
      dark: "/brand/norican-secondary-2026-blue.png",
    },
    // 1113 x 294 (blue), 1112 x 294 (white)
    aspectRatio: 3.7857,
    minBoxHeight: 19,
    defaultHeight: 24,
  },
  mark: {
    src: {
      white: "/brand/norican-icon-white.svg",
      dark: "/brand/norican-icon-blue.svg",
    },
    // viewBox 797.24 x 798.84
    aspectRatio: 0.998,
    minBoxHeight: 10,
    defaultHeight: 20,
  },
};

export interface NoricanLogoProps {
  /** Which brand asset to show; see the file comment for when each one applies. */
  kind?: NoricanLogoKind;
  /** Which colourway; `auto` follows the app theme. */
  variant?: "auto" | "white" | "dark";
  /**
   * Box height in px. It sets the rendered height, reserves the box before the file
   * loads, and is checked against the guide's minimum (p.17).
   */
  height?: NoricanLogoHeight;
  /** Spacing classes only (margins); never a height class. */
  className?: string;
}

export function NoricanLogo({
  kind = "primary",
  variant = "auto",
  height,
  className,
}: NoricanLogoProps) {
  const { theme } = useAppTheme();
  const asset = ASSETS[kind];
  const resolved = variant === "auto" ? (theme === "dark" ? "white" : "dark") : variant;
  const boxHeight = height ?? asset.defaultHeight;

  if (import.meta.env.DEV) {
    if (boxHeight < asset.minBoxHeight) {
      logger.warn(
        "[brand]",
        `NoricanLogo kind=${kind} at ${boxHeight}px is below the brand minimum box of ${asset.minBoxHeight}px (Brand Guidelines 3.0 p.17)`,
      );
    }
    if (className && HEIGHT_UTILITY.test(className)) {
      logger.warn(
        "[brand]",
        `NoricanLogo kind=${kind} got a height class in className ("${className}"); size it with the height prop so the minimum-size check applies`,
      );
    }
  }

  return (
    <img
      src={asset.src[resolved]}
      // Decorative: the logo always sits beside the product name, so announcing
      // both would read the company twice.
      alt=""
      aria-hidden="true"
      height={boxHeight}
      width={Math.round(boxHeight * asset.aspectRatio)}
      className={cn("w-auto shrink-0 select-none", className, HEIGHT_CLASS[boxHeight])}
      draggable={false}
    />
  );
}
